import { SetStateAction, useState } from "react";
import { z } from "zod";

import { StorageProps, useStorage } from "@smart/itops-hooks-dom";

import {
  ReminderMessageFilter,
  categorySchema,
  creatorsSchema,
  filtersSchema,
  locationsSchema,
  matterTypesSchema,
  searchSchema,
} from "./storage-keys";

type ListState = {
  category: z.infer<typeof categorySchema>;
  filters: z.infer<typeof filtersSchema>;
  locations: z.infer<typeof locationsSchema>;
  matterTypes: z.infer<typeof matterTypesSchema>;
  creators: z.infer<typeof creatorsSchema>;
  reminderMessages: ReminderMessageFilter[];
  search: z.infer<typeof searchSchema>;
};

// review list for this page load, survives opening a form and coming back
let reviewView: ListState | undefined;

export const startReviewView = (value: ReminderMessageFilter) => {
  reviewView = {
    category: undefined,
    filters: ["byReminderMessage"],
    locations: [],
    matterTypes: [],
    creators: [],
    reminderMessages: [value],
    search: "",
  };
};

export const endReviewView = () => {
  reviewView = undefined;
};

export const isReviewView = () => !!reviewView;

// saved list state unless review is open, then edits stay there
export const useListState = <K extends keyof ListState, V extends ListState[K]>(
  field: K,
  options: StorageProps<V>,
): [V, (next: SetStateAction<V>) => void] => {
  const [inReview] = useState(() => !!reviewView);
  const stored = useStorage(options);
  const [memory, setMemory] = useState(() => reviewView?.[field] as V);
  if (!inReview) return stored;

  const set = (next: SetStateAction<V>) =>
    setMemory((previous) => {
      const value = next instanceof Function ? next(previous) : next;
      if (reviewView) reviewView[field] = value;
      return value;
    });
  return [memory, set];
};
